/** Carousels with the options the theme's scripts used. */
import Swiper from 'swiper'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Fancybox } from '@fancyapps/ui/dist/fancybox/fancybox.esm.js'
import { slick, type SlickOptions } from './slick'

import type { SwiperOptions } from 'swiper/types'

const swiper = (el: HTMLElement, options: SwiperOptions) => new Swiper(el, { modules: [Navigation, Pagination, Autoplay], ...options })

const each = (sel: string, fn: (el: HTMLElement) => void) => document.querySelectorAll<HTMLElement>(sel).forEach(fn)

const newsSlick: SlickOptions = {
  slidesToShow: 2,
  slidesToScroll: 1,
  dots: true,
  dotsClass: 'dots-style',
  autoplaySpeed: 2000,
  responsive: [{ breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 2, infinite: true, dots: true } }],
}

function slickCarousels() {
  each('.background_news .news', (el) => slick(el, newsSlick))
  each('.specialisation_slide', (el) => slick(el, { ...newsSlick, slidesToShow: 3 }))
  each('.multiple-items', (el) =>
    slick(el, {
      infinite: true,
      slidesToShow: 4,
      slidesToScroll: 5,
      dots: true,
      dotsClass: 'dots-style',
      responsive: [
        { breakpoint: 1025, settings: { slidesToShow: 4, slidesToScroll: 4 } },
        { breakpoint: 688, settings: { slidesToShow: 2, slidesToScroll: 2 } },
      ],
    }),
  )
  each('.wrapper_table', (el) =>
    slick(el, {
      dots: true,
      dotsClass: 'dots-style',
      infinite: true,
      autoplay: false,
      autoplaySpeed: 5000,
      speed: 300,
      slidesToShow: 6,
      slidesToScroll: 6,
      responsive: [
        { breakpoint: 1024, settings: { slidesToShow: 3, slidesToScroll: 3, infinite: true, dots: true } },
        { breakpoint: 600, settings: { slidesToShow: 2, slidesToScroll: 2 } },
        { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 6 } },
      ],
    }),
  )
  // WordPress also ran Slick on the Swiper wrapper of /blagodarstvennie/; that double init is not reproduced.
  each('.wrapper_reviews:not(.swiper-wrapper)', (el) =>
    slick(el, {
      dots: true,
      dotsClass: 'dots-style',
      infinite: true,
      autoplay: true,
      autoplaySpeed: 5000,
      speed: 300,
      slidesToShow: 2,
      slidesToScroll: 2,
      responsive: [
        { breakpoint: 1024, settings: { slidesToShow: 1, slidesToScroll: 1, infinite: true, dots: true } },
        { breakpoint: 600, settings: { slidesToShow: 1, slidesToScroll: 1 } },
        { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 3 } },
      ],
    }),
  )
  each('.responsive', (el) =>
    slick(el, {
      infinite: true,
      dots: true,
      dotsClass: 'dots-style',
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 500,
      autoplay: false,
      autoplaySpeed: 3000,
    }),
  )
  each('.customer-logos', (el) =>
    slick(el, {
      slidesToShow: 6,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 1500,
      dots: false,
      pauseOnHover: false,
      responsive: [
        { breakpoint: 768, settings: { slidesToShow: 2 } },
        { breakpoint: 520, settings: { slidesToShow: 2 } },
      ],
    }),
  )
}

function swipers() {
  each('.mySwiper1', (el) => swiper(el, { loop: true, pagination: { el: '.swiper-pagination', clickable: true } }))
  each(
    '.mySwiper3',
    (el) =>
      swiper(el, {
        slidesPerView: 5,
        spaceBetween: 30,
        loop: true,
        autoplay: { delay: 3000, disableOnInteraction: false },
        pagination: { el: '.swiper-pagination', clickable: true, dynamicBullets: true, dynamicMainBullets: 5 },
        breakpoints: {
          320: { slidesPerView: 2.5, spaceBetween: 15, centeredSlides: false },
          480: { slidesPerView: 3, spaceBetween: 20 },
          768: { slidesPerView: 4, spaceBetween: 25 },
          1024: { slidesPerView: 5, spaceBetween: 30 },
          1200: { slidesPerView: 6, spaceBetween: 30 },
        },
      }),
  )
  each(
    '.SwiperReviews',
    (el) =>
      swiper(el, {
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        pagination: { el: '.swiper-pagination', clickable: true },
        navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
        breakpoints: { 768: { slidesPerView: 2 }, 1024: { slidesPerView: 4 } },
        autoplay: { delay: 5000, disableOnInteraction: false },
      }),
  )
  // The reviews page and review cards use different settings for the same class.
  const reviewsPage = Boolean(document.querySelector('.reviews-page__list'))
  each('.reviews__slider', (el) => {
    if (!el.classList.contains('swiper') && !el.querySelector('.swiper-wrapper')) return
    swiper(
      el,
      reviewsPage
        ? {
            slidesPerView: 2,
            spaceBetween: 20,
            loop: true,
            pagination: { el: '.swiper-pagination', clickable: true },
            navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
            breakpoints: { 768: { slidesPerView: 2, spaceBetween: 30 }, 1024: { slidesPerView: 3, spaceBetween: 40 } },
          }
        : {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: false,
            pagination: { el: el.querySelector<HTMLElement>('.swiper-pagination'), clickable: true },
            navigation: { nextEl: el.querySelector<HTMLElement>('.swiper-button-next'), prevEl: el.querySelector<HTMLElement>('.swiper-button-prev') },
            breakpoints: { 768: { slidesPerView: 2, spaceBetween: 30 }, 1024: { slidesPerView: 2, spaceBetween: 30 } },
          },
    )
  })
}

function lightboxes() {
  Fancybox.bind('[data-fancybox^="popup-"]', {})
  Fancybox.bind('[data-fancybox="docs"]', { infinite: true, dragToClose: true } as never)
  Fancybox.bind('[data-fancybox="reviews"]', { infinite: true, dragToClose: true } as never)
  Fancybox.bind('[data-fancybox="reviews-single"]', {
    infinite: true,
    dragToClose: true,
    caption: (_f: unknown, slide: { triggerEl?: HTMLElement }) => slide.triggerEl?.dataset.caption || '',
  } as never)
}

export function initSliders() {
  slickCarousels()
  swipers()
  lightboxes()
}
